import type {
  WidgetConfig,
  WidgetWeekEntry,
  WorkoutLogConfig,
  WorkoutLogDayData,
} from "@/lib/schema";
import { countPhrase, sumOverDays } from "@/widgets/describe";
import { getWidgetModule } from "@/widgets/registry";
import type { DayWidgetModule, ScorablePart } from "@/widgets/types";
import { DayView } from "./DayView";
import { SettingsEditor } from "./SettingsEditor";
import {
  maxScoreWeek,
  scoreDay,
  scoreWeek,
  scoreWeekPlanning,
  scoreWeekWorkouts,
  workoutsWeekGoal,
} from "./scoring";
import { validate } from "./validate";

function defaults(existing: WidgetConfig[]): WorkoutLogConfig {
  const dayWidgets = existing.filter(
    (w) => getWidgetModule(w.kind)?.scope === "day",
  );
  const maxRow = dayWidgets.reduce((m, w) => Math.max(m, w.row), -1);
  return {
    id: crypto.randomUUID(),
    kind: "workout-log",
    label: "Treningslogg",
    row: maxRow + 1,
    column: 0,
    enabled: true,
    points: 2,
    weekGoal: Number.NaN,
  };
}

// Hviledager are named only when there are no økter to name instead: both are
// things the user declared, but "4 økter" is the more alarming loss.
function describeWeekData(entry: WidgetWeekEntry): string | null {
  const workouts = sumOverDays<WorkoutLogDayData>(
    entry,
    (day) => day?.workouts?.length ?? 0,
  );
  if (workouts > 0) return countPhrase(workouts, "økt", "økter");
  const restDays = sumOverDays<WorkoutLogDayData>(entry, (day) =>
    day?.restDay ? 1 : 0,
  );
  return countPhrase(restDays, "hviledag", "hviledager");
}

/**
 * Switching planning off in the editor leaves its keys behind as undefined;
 * a stored config either has planning or has neither key.
 */
function normalize(config: WorkoutLogConfig): WorkoutLogConfig {
  if (config.planning) return config;
  const { planning: _planning, workoutsWeekGoal: _workouts, ...rest } = config;
  return rest;
}

/**
 * Økter and Planlegging (docs/adr/0010). Planlegging exists only while
 * planning is switched on; without it the instance is one whole again. Keys
 * are stored inside sealed snapshots and must not change.
 */
const parts: ScorablePart<WorkoutLogConfig>[] = [
  {
    key: "workouts",
    label: "Økter",
    scoreWeek: scoreWeekWorkouts,
    weekGoal: workoutsWeekGoal,
  },
  {
    key: "planning",
    label: "Planlegging",
    scoreWeek: scoreWeekPlanning,
    weekGoal: (config) => config.planning?.weekGoal ?? 0,
    appliesTo: (config) => config.planning !== undefined,
  },
];

export const workoutLog: DayWidgetModule<WorkoutLogConfig> = {
  kind: "workout-log",
  label: "Treningslogg",
  scope: "day",
  scoring: "points",
  defaults,
  validate,
  normalize,
  SettingsEditor,
  DayView,
  describeWeekData,
  scoreDay,
  scoreWeek,
  maxScoreWeek,
  parts,
};
